/* =========================================================
   ODP custom JS (Outdoorphoto)
   Loaded deferred from layout/theme.liquid.
   ========================================================= */

/* ---------------------------------------------------------
   ODP CUSTOM: SKU copy chip on product cards
   Markup: snippets/card-product.liquid  (button.op-sku[data-op-sku])
   - One delegated listener on document, so cards injected later by
     filtering, sorting, "load more" or infinite scroll work without re-binding.
   - Copies the FULL SKU from data-op-sku (the visible text may be truncated).
   - Shows "Copied" on the chip for 1.5s and announces it to screen readers.
   --------------------------------------------------------- */
(() => {
  if (window.ODPSkuCopy) return; // guard against double-loading
  window.ODPSkuCopy = true;

  const COPIED_LABEL = 'Copied';
  const RESET_MS = 1500;

  // One shared live region for screen-reader announcements
  let liveRegion;
  const announce = (message) => {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(() => (liveRegion.textContent = message), 50);
  };

  // Clipboard API with a fallback for older browsers / non-secure previews
  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'absolute';
    field.style.left = '-9999px';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    if (!ok) throw new Error('Copy failed');
  };

  document.addEventListener('click', async (event) => {
    const chip = event.target.closest('.op-sku[data-op-sku]');
    if (!chip) return;

    // The chip sits inside the product card: never let the click navigate
    event.preventDefault();
    event.stopPropagation();

    const sku = chip.dataset.opSku;
    const textEl = chip.querySelector('.op-sku__text');
    if (!sku || !textEl) return;

    try {
      await copyText(sku);
    } catch (error) {
      announce('Could not copy SKU');
      return;
    }

    window.clearTimeout(chip._odpTimer);
    chip.classList.add('is-copied');
    textEl.textContent = COPIED_LABEL;
    announce(`SKU ${sku} copied`);

    chip._odpTimer = window.setTimeout(() => {
      chip.classList.remove('is-copied');
      textEl.textContent = sku;
    }, RESET_MS);
  });
})();
/* ODP CUSTOM END: SKU copy chip */

/* ==========================================================================
   ODP: Read more (main-page description). Append to assets/odp-custom.js
   - Hides the button when the text already fits (re-checked on resize)
   - aria-expanded + label swap; observer disconnected on section re-render
   ========================================================================== */
if (!customElements.get('odp-read-more')) {
  customElements.define('odp-read-more', class extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.odp-read-more__content');
      this.button = this.querySelector('.odp-read-more__toggle');
      if (!this.content || !this.button) return;

      this.label = this.button.querySelector('.btn__text') || this.button;
      this.onClick = () => this.toggle();
      this.button.addEventListener('click', this.onClick);

      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this);
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
    }

    update() {
      if (this.hasAttribute('expanded')) return;
      const fits = this.content.scrollHeight <= this.content.clientHeight + 1;
      this.classList.toggle('is-short', fits);
      this.button.hidden = fits;
    }

    toggle() {
      const open = !this.hasAttribute('expanded');
      this.toggleAttribute('expanded', open);
      this.button.setAttribute('aria-expanded', String(open));
      this.label.textContent = open ? this.button.dataset.less : this.button.dataset.more;
      if (!open) {
        this.update();
        if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  });
}

/* =========================================================
   APPEND TO assets/odp-custom.js
   ODP: Open now. Reads data-odp-hours (schema.org format, e.g.
   "Mo-Th 08:00-17:30, Fr 08:00-17:00, Sa 08:00-14:00") and shows
   "Open now · closes 17:30" or "Closed · opens Mon 08:00",
   always in South African time, whatever the visitor's timezone.
   Public holidays aren't handled.
   ========================================================= */
(() => {
  const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const LABEL = { Su: 'Sun', Mo: 'Mon', Tu: 'Tue', We: 'Wed', Th: 'Thu', Fr: 'Fri', Sa: 'Sat' };
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  function parse(spec) {
    const week = {};
    spec.split(',').forEach((part) => {
      const m = part.trim().match(/^([A-Z][a-z])(?:-([A-Z][a-z]))?\s+(\d{1,2}:\d{2})-(\d{1,2}:\d{2})$/);
      if (!m) return;
      const from = DAYS.indexOf(m[1]);
      const to = DAYS.indexOf(m[2] || m[1]);
      if (from < 0 || to < 0) return;
      for (let d = from; ; d = (d + 1) % 7) {
        week[d] = [toMin(m[3]), toMin(m[4]), m[3], m[4]];
        if (d === to) break;
      }
    });
    return week;
  }

  function nowInSA() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type).value;
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    return { day, min: Number(get('hour')) * 60 + Number(get('minute')) };
  }

  function render(el) {
    const week = parse(el.dataset.odpHours || '');
    if (!Object.keys(week).length) return;
    const { day, min } = nowInSA();
    const today = week[day];
    let text;
    let open = false;

    if (today && min >= today[0] && min < today[1]) {
      open = true;
      text = `Open now · closes ${today[3]}`;
    } else {
      for (let i = 0; i < 7; i++) {
        const d = (day + i) % 7;
        const slot = week[d];
        if (!slot || (i === 0 && min >= slot[0])) continue;
        const when = i === 0 ? 'today' : i === 1 ? 'tomorrow' : LABEL[DAYS[d]];
        text = `Closed · opens ${when} ${slot[2]}`;
        break;
      }
      text = text || 'Closed';
    }

    el.textContent = text;
    el.classList.toggle('is-open', open);
    el.classList.toggle('is-closed', !open);
    el.hidden = false;
  }

  function init(root = document) {
    root.querySelectorAll('[data-odp-hours]').forEach(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  // Theme editor: re-run when a section is re-rendered
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* ============================================================
   OUTDOORPHOTO | PDP EXTRAS v4
   WhatsApp Product Enquiry

   File: assets/odp-custom.js

   Features:
   - Compatible with odp-pdp-extras.liquid v4
   - Detects the currently selected product variant
   - Includes product title, SKU, price and URL
   - Supports Shopify Hyper's product forms
   - Works with dynamically reloaded Shopify sections
   - Uses event delegation for better performance
   - No external libraries required
   - Falls back to the original Liquid-generated URL
   ============================================================ */

(function () {
  'use strict';

  // Prevent duplicate event listeners.
  if (window.odpPdpWhatsappEnhanced) return;

  window.odpPdpWhatsappEnhanced = true;

  /* ----------------------------------------------------------
     FIND THE SELECTED PRODUCT VARIANT
     ---------------------------------------------------------- */

  function getSelectedVariant(wrapper, variants) {

    // Locate the current Shopify product section.
    var section =
      wrapper.closest('.shopify-section') || document;

    // Find product Add to Cart forms.
    var forms = section.querySelectorAll(
      'form[action*="/cart/add"]'
    );

    var selectedId = '';

    // Find the selected variant from the active product form.
    for (var i = 0; i < forms.length; i++) {

      var form = forms[i];

      // Ignore forms that are not currently visible.
      if (!form.getClientRects().length) continue;

      var input = form.querySelector(
        '[name="id"]'
      );

      if (
        input &&
        !input.disabled &&
        input.value
      ) {

        var candidate = String(input.value);

        // Only accept variants belonging to this product.
        var exists = variants.some(function (variant) {
          return String(variant.id) === candidate;
        });

        if (exists) {
          selectedId = candidate;
          break;
        }

      }

    }

    // Fallback to the variant ID in the product URL.
    if (!selectedId) {

      var urlParams = new URLSearchParams(
        window.location.search
      );

      selectedId = urlParams.get('variant') || '';

    }

    // Find the matching variant.
    var selectedVariant = variants.find(function (variant) {
      return String(variant.id) === selectedId;
    });

    // Return null if the variant cannot be determined.
    // This preserves the original Liquid-generated URL.
    return selectedVariant || null;

  }

  /* ----------------------------------------------------------
     BUILD THE WHATSAPP MESSAGE
     ---------------------------------------------------------- */

  function buildWhatsAppMessage(link, variant) {

    var productTitle =
      link.dataset.odpProductTitle || '';

    var productUrl =
      link.dataset.odpProductUrl || window.location.href;

    // Create the product URL.
    var url = new URL(
      productUrl,
      window.location.origin
    );

    // Include the selected variant.
    url.searchParams.set(
      'variant',
      variant.id
    );

    // Build the customer enquiry.
    var message =
      'Hi Outdoorphoto!\n\n' +

      "I'd like to enquire about this product:\n\n" +

      productTitle + '\n' +

      (
        variant.sku
          ? 'SKU: ' + variant.sku + '\n'
          : ''
      ) +

      (
        variant.price
          ? 'Price: ' + variant.price + '\n'
          : ''
      ) +

      '\nProduct link:\n' +

      url.toString() +

      '\n\nCould you please assist me? Thank you!';

    return message;

  }

  /* ----------------------------------------------------------
     HANDLE WHATSAPP ENQUIRIES
     ---------------------------------------------------------- */

  document.addEventListener('click', function (event) {

    // Detect the WhatsApp link.
    var link = event.target.closest(
      '[data-odp-pdp-wa]'
    );

    if (!link) return;

    // Find the PDP Extras wrapper.
    var wrapper = link.closest(
      '[data-odp-pdp-extras]'
    );

    if (!wrapper) return;

    // Locate the variant data generated by Liquid.
    var variantsElement = wrapper.querySelector(
      '[data-odp-pdp-variants]'
    );

    if (!variantsElement) return;

    try {

      // Read product variant information.
      var variants = JSON.parse(
        variantsElement.textContent
      );

      if (!Array.isArray(variants) || !variants.length) {
        return;
      }

      // Determine the currently selected variant.
      var selectedVariant = getSelectedVariant(
        wrapper,
        variants
      );

      if (!selectedVariant) return;

      // Get the WhatsApp Business number.
      var whatsappNumber =
        link.dataset.odpWaNumber || '';

      // Do not generate an invalid WhatsApp URL.
      if (!/^\d{10,15}$/.test(whatsappNumber)) {
        return;
      }

      // Generate the personalised message.
      var message = buildWhatsAppMessage(
        link,
        selectedVariant
      );

      // Construct the WhatsApp URL.
      var whatsappUrl =
        'https://wa.me/' +
        whatsappNumber +
        '?text=' +
        encodeURIComponent(message);

      // Update the link before browser navigation.
      link.href = whatsappUrl;

    } catch (error) {

      // Keep the original Liquid-generated WhatsApp link.
      // This prevents errors from breaking the PDP.

    }

  }, true);

})();

   /* ==========================================================
   OUTDOORPHOTO - PAYMENT OPTIONS V2
   Lightweight PDP payment widget

   Features:
   - Accessible payment modal
   - Keyboard navigation and focus trapping
   - Dynamic Shopify variant pricing
   - Payflex and Happy Pay instalment estimates
   - Shopify Theme Editor compatibility
   - Efficient event delegation
   - Cached DOM references
   - Automatic cleanup of removed sections

   Liquid: snippets/odp-payment-options-widget.liquid
   CSS: assets/odp-custom.css
   JS: assets/odp-custom.js
   ========================================================== */

(() => {
  'use strict';

  // Prevent duplicate script initialisation.
  if (window.__odpPaymentOptionsV2) return;

  window.__odpPaymentOptionsV2 = true;


  /* ========================================================
     CONFIGURATION
     ======================================================== */

  const SELECTORS = {
    widget: '[data-odp-payment-widget]',
    open: '[data-odp-payment-open]',
    modal: '[data-odp-payment-modal]',
    close: '[data-odp-payment-close]',
    tab: '[data-odp-payment-tab]',
    panel: '[data-odp-payment-panel]',
    payflex: '[data-odp-payflex-amount]',
    happyPay: '[data-odp-happypay-amount]'
  };

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  // Create the currency formatter once.
  const currencyFormatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Store widget instances and their DOM references.
  const instances = new Map();

  let activeInstance = null;
  let previousFocus = null;
  let savedScrollStyles = null;


  /* ========================================================
     CURRENCY FORMATTING
     ======================================================== */

  function formatMoney(cents) {
    return `R ${currencyFormatter.format(cents / 100)}`;
  }


  /* ========================================================
     ACCESSIBILITY - FOCUSABLE ELEMENTS
     ======================================================== */

  function getFocusableElements(modal) {
    return Array.from(
      modal.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter((element) => {
      return (
        !element.closest('[hidden], [inert]') &&
        element.getClientRects().length > 0
      );
    });
  }


  /* ========================================================
     PAGE SCROLL LOCK
     ======================================================== */

  function lockScroll() {
    const body = document.body;

    // Calculate scrollbar width to prevent layout shifting.
    const scrollbarWidth =
      window.innerWidth -
      document.documentElement.clientWidth;

    savedScrollStyles = {
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight
    };

    if (scrollbarWidth > 0) {
      const currentPadding =
        parseFloat(getComputedStyle(body).paddingRight) || 0;

      body.style.paddingRight =
        `${currentPadding + scrollbarWidth}px`;
    }

    body.style.overflow = 'hidden';

    document.documentElement.classList.add(
      'odp-payx-lock'
    );
  }


  function unlockScroll() {
    if (!savedScrollStyles) return;

    document.documentElement.classList.remove(
      'odp-payx-lock'
    );

    document.body.style.overflow =
      savedScrollStyles.overflow;

    document.body.style.paddingRight =
      savedScrollStyles.paddingRight;

    savedScrollStyles = null;
  }


  /* ========================================================
     OPEN PAYMENT MODAL
     ======================================================== */

  function openModal(instance) {
    if (activeInstance === instance) return;

    // Close another payment modal if already open.
    if (activeInstance) {
      closeModal();
    }

    const { modal, button } = instance;

    previousFocus = document.activeElement;

    // Move the modal to body to avoid clipping by
    // Shopify Hyper theme containers and stacking contexts.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    activeInstance = instance;

    modal.hidden = false;

    button.setAttribute(
      'aria-expanded',
      'true'
    );

    lockScroll();

    // Enable global keyboard handling only while open.
    document.addEventListener(
      'keydown',
      handleModalKeyboard
    );

    // Focus the close button when the modal opens.
    const closeButton = modal.querySelector(
      '.odp-payx-modal__close'
    );

    if (closeButton) {
      closeButton.focus();
    } else {
      modal.focus();
    }
  }


  /* ========================================================
     CLOSE PAYMENT MODAL
     ======================================================== */

  function closeModal() {
    if (!activeInstance) return;

    const { modal, button } = activeInstance;

    activeInstance = null;

    modal.hidden = true;

    button.setAttribute(
      'aria-expanded',
      'false'
    );

    document.removeEventListener(
      'keydown',
      handleModalKeyboard
    );

    unlockScroll();

    // Return focus to the element used before opening.
    const returnFocus = previousFocus?.isConnected
      ? previousFocus
      : button;

    previousFocus = null;

    if (returnFocus?.isConnected) {
      returnFocus.focus();
    }
  }


  /* ========================================================
     MODAL KEYBOARD ACCESSIBILITY
     ======================================================== */

  function handleModalKeyboard(event) {
    if (!activeInstance) return;

    // Escape closes the modal.
    if (event.key === 'Escape') {
      event.preventDefault();

      closeModal();

      return;
    }

    // Only handle Tab navigation below.
    if (event.key !== 'Tab') return;

    const modal = activeInstance.modal;

    const focusable = getFocusableElements(modal);

    if (!focusable.length) {
      event.preventDefault();

      modal.focus();

      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const current = document.activeElement;

    // Shift + Tab from the first element wraps to the last.
    if (
      event.shiftKey &&
      (
        current === first ||
        !modal.contains(current)
      )
    ) {
      event.preventDefault();

      last.focus();

      return;
    }

    // Tab from the last element wraps to the first.
    if (
      !event.shiftKey &&
      (
        current === last ||
        !modal.contains(current)
      )
    ) {
      event.preventDefault();

      first.focus();
    }
  }


  /* ========================================================
     PAYMENT TAB SWITCHING
     ======================================================== */

  function activateTab(instance, tabName) {
    // Update tab buttons.
    instance.tabs.forEach((button) => {
      const isActive =
        button.dataset.odpPaymentTab === tabName;

      button.classList.toggle(
        'is-active',
        isActive
      );

      button.setAttribute(
        'aria-selected',
        String(isActive)
      );

      button.tabIndex = isActive ? 0 : -1;
    });

    // Show the matching content panel.
    instance.panels.forEach((panel) => {
      const isActive =
        panel.dataset.odpPaymentPanel === tabName;

      panel.classList.toggle(
        'is-active',
        isActive
      );

      panel.hidden = !isActive;
    });
  }


  /* ========================================================
     PAYMENT TAB KEYBOARD NAVIGATION
     ======================================================== */

  function handleTabKeyboard(event, instance) {
    const button = event.target.closest(
      SELECTORS.tab
    );

    if (!button) return;

    const tabs = instance.tabs;

    const currentIndex = tabs.indexOf(button);

    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex =
          (currentIndex - 1 + tabs.length) % tabs.length;
        break;

      case 'Home':
        nextIndex = 0;
        break;

      case 'End':
        nextIndex = tabs.length - 1;
        break;

      default:
        return;
    }

    event.preventDefault();

    const nextButton = tabs[nextIndex];

    activateTab(
      instance,
      nextButton.dataset.odpPaymentTab
    );

    nextButton.focus();
  }


  /* ========================================================
     UPDATE PAYMENT INSTALMENT AMOUNTS
     ======================================================== */

  function updateInstancePrice(instance, newPriceCents) {
    const parsedPrice = Number(newPriceCents);

    // Reject invalid or negative prices.
    if (
      !Number.isFinite(parsedPrice) ||
      parsedPrice < 0
    ) {
      return;
    }

    const priceCents = Math.round(parsedPrice);

    // Avoid unnecessary DOM updates.
    if (priceCents === instance.priceCents) {
      return;
    }

    instance.priceCents = priceCents;

    instance.widget.dataset.productPriceCents =
      String(priceCents);

    // Payflex: estimated four equal instalments.
    if (instance.payflex) {
      const payflexAmount = Math.round(
        priceCents / 4
      );

      instance.payflex.textContent =
        formatMoney(payflexAmount);
    }

    // Happy Pay: estimated two equal instalments.
    if (instance.happyPay) {
      const happyPayAmount = Math.round(
        priceCents / 2
      );

      instance.happyPay.textContent =
        formatMoney(happyPayAmount);
    }
  }


  /* ========================================================
     INITIALISE ONE PAYMENT WIDGET
     ======================================================== */

  function initialiseWidget(widget) {
    // Prevent duplicate event listeners.
    if (instances.has(widget)) return;

    const button = widget.querySelector(
      SELECTORS.open
    );

    const modal = widget.querySelector(
      SELECTORS.modal
    );

    if (!button || !modal) return;

    // Cache elements before the modal moves into body.
    const instance = {
      widget,
      button,
      modal,

      tabs: Array.from(
        modal.querySelectorAll(SELECTORS.tab)
      ),

      panels: Array.from(
        modal.querySelectorAll(SELECTORS.panel)
      ),

      payflex: modal.querySelector(
        SELECTORS.payflex
      ),

      happyPay: modal.querySelector(
        SELECTORS.happyPay
      ),

      priceCents: Number(
        widget.dataset.productPriceCents
      )
    };

    // Allow programmatic focus on the dialog itself.
    modal.tabIndex = -1;

    instances.set(widget, instance);


    /* ------------------------------------------------------
       Open modal
       ------------------------------------------------------ */

    button.addEventListener('click', () => {
      openModal(instance);
    });


    /* ------------------------------------------------------
       Modal click handling
       ------------------------------------------------------ */

    modal.addEventListener('click', (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      // Close from the backdrop, X button, or overlay.
      if (
        target === modal ||
        target.closest(SELECTORS.close)
      ) {
        event.preventDefault();

        closeModal();

        return;
      }

      // Switch between payment tabs.
      const selectedTab = target.closest(
        SELECTORS.tab
      );

      if (
        selectedTab &&
        modal.contains(selectedTab)
      ) {
        activateTab(
          instance,
          selectedTab.dataset.odpPaymentTab
        );
      }
    });


    /* ------------------------------------------------------
       Payment tab keyboard navigation
       ------------------------------------------------------ */

    modal.addEventListener('keydown', (event) => {
      handleTabKeyboard(event, instance);
    });
  }


  /* ========================================================
     CLEAN UP REMOVED SHOPIFY SECTIONS
     ======================================================== */

  function removeInstance(widget) {
    const instance = instances.get(widget);

    if (!instance) return;

    // Close the modal if this widget owns it.
    if (activeInstance === instance) {
      closeModal();
    }

    // Remove the modal, including when portalled to body.
    instance.modal.remove();

    instances.delete(widget);
  }


  function cleanupRemovedWidgets() {
    for (const widget of instances.keys()) {
      if (!widget.isConnected) {
        removeInstance(widget);
      }
    }
  }


  /* ========================================================
     INITIALISE ALL PAYMENT WIDGETS
     ======================================================== */

  function initialiseAll(root = document) {
    cleanupRemovedWidgets();

    // Handle the root itself if it is a widget.
    if (root.matches?.(SELECTORS.widget)) {
      initialiseWidget(root);
    }

    // Initialise widgets inside the root.
    root.querySelectorAll(SELECTORS.widget)
      .forEach(initialiseWidget);
  }


  /* ========================================================
     PUBLIC API - SHOPIFY VARIANT PRICE UPDATES
     ======================================================== */

  window.OutdoorphotoPaymentOptions = {
    ...window.OutdoorphotoPaymentOptions,

    /**
     * Update instalment estimates after a variant change.
     *
     * @param {number} priceCents
     * Shopify variant price in cents.
     *
     * Example:
     * OutdoorphotoPaymentOptions.update(3989500);
     */

    update(priceCents) {
      cleanupRemovedWidgets();

      instances.forEach((instance) => {
        updateInstancePrice(
          instance,
          priceCents
        );
      });
    }
  };


  /* ========================================================
     INITIAL PAGE LOAD
     ======================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseAll(),
      { once: true }
    );
  } else {
    initialiseAll();
  }


  /* ========================================================
     SHOPIFY THEME EDITOR SUPPORT
     ======================================================== */

  // Reinitialise widgets when Shopify loads a section.
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseAll(event.target);
    }
  );

  // Clean up instances before Shopify removes a section.
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      for (const widget of Array.from(instances.keys())) {
        if (event.target.contains(widget)) {
          removeInstance(widget);
        }
      }
    }
  );

})();

/* PDP product meta: copy SKU / GTIN (ODP, Hyper v1.5.0)
   Delegated, so it survives product-info.js re-rendering the info blocks on a variant switch.
   Copies the value <span> only ("CAMNIILCZ6III", not "SKU: CAMNIILCZ6III"). */
(() => {
  if (window.__odpMetaCopy) return;
  window.__odpMetaCopy = true;

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
    // Fallback for non-secure contexts / older Safari
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    return ok ? Promise.resolve() : Promise.reject(new Error('copy failed'));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-odp-copy]');
    if (!button) return;

    const source = document.getElementById(button.dataset.odpCopy);
    const value = source?.querySelector('span:last-child')?.textContent.trim();
    if (!value) return;

    copyText(value)
      .then(() => {
        button.classList.add('is-copied');
        const status = button.closest('.odp-meta')?.querySelector('[data-odp-copy-status]');
        if (status) status.textContent = `${button.dataset.copiedLabel || 'Copied'}: ${value}`;
        clearTimeout(button._odpCopyTimer);
        button._odpCopyTimer = setTimeout(() => {
          button.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, 1600);
      })
      .catch(() => {});
  });
})();
/* =====================================================================
   ODP · Buy box ("Refined") — snippets/buy-buttons.liquid (Hyper v1.5.0)
   Append to assets/odp-custom.js. No dependencies; safe to load before or
   after theme.js (it waits for FoxTheme.pubsub).
   ===================================================================== */
(() => {
  if (window.__odpBuyBox) return;
  window.__odpBuyBox = true;

  const ADDED_MS = 2500;

  // 1) "Added" tick. theme.js ProductForm fires product-ajax:added before its finally() removes
  //    .btn--loading, so the buttons still loading at that moment are the ones that just added.
  document.addEventListener('product-ajax:added', () => {
    document.querySelectorAll('.odp-atc.btn--loading').forEach((button) => {
      button.classList.add('is-added');
      const status = button.closest('.odp-buy')?.querySelector('[data-odp-buy-status]');
      if (status) status.textContent = status.dataset.added || '';
      clearTimeout(button._odpAddedTimer);
      button._odpAddedTimer = setTimeout(() => {
        button.classList.remove('is-added');
        if (status) status.textContent = '';
      }, ADDED_MS);
    });
  });

  // A variant switch or a new submit ends the "Added" state at once.
  const clearAdded = (root = document) =>
    root.querySelectorAll('.odp-atc.is-added').forEach((button) => {
      clearTimeout(button._odpAddedTimer);
      button.classList.remove('is-added');
    });
  document.addEventListener('submit', (event) => clearAdded(event.target), true);

  // 2) Enquire now follows the selected variant (product-info.js publishes variantChange, both
  //    optimistically and after the section render; variant is null for a combination that doesn't exist).
  const onVariantChange = ({ data } = {}) => {
    if (!data?.sectionId) return;
    document.querySelectorAll(`.odp-buy[data-section-id="${CSS.escape(data.sectionId)}"]`).forEach((buyBox) => {
      clearAdded(buyBox);
      const enquire = buyBox.querySelector('[data-odp-enquire]');
      if (enquire) enquire.hidden = !data.variant || data.variant.available !== false;
    });
  };
  const subscribe = () => {
    const pubsub = window.FoxTheme?.pubsub;
    if (!pubsub) return;
    pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, onVariantChange);
  };
  if (window.FoxTheme?.pubsub) subscribe();
  else document.addEventListener('DOMContentLoaded', subscribe, { once: true });

  // 3) Enquire now opens the "Ask a question" popup with a prefilled message when it exists;
  //    otherwise the link goes to the contact page as normal.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-odp-enquire][data-modal-id]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.modalId);
    if (!modal || typeof modal.show !== 'function') return;
    event.preventDefault();
    const message = modal.querySelector('textarea');
    if (message && !message.value.trim()) message.value = trigger.dataset.message || '';
    modal.show(trigger);
  });
})();

/* ==========================================================================
   ODP CUSTOM — Pairs well with: <odp-paged-rail>
   Append to assets/odp-custom.js
   Native scroll-snap pager: arrows + dots for snippets/product-complementary.liquid.
   Works when product-recommendations.js swaps the markup in (custom elements upgrade on insert).
   ========================================================================== */
if (!customElements.get('odp-paged-rail')) {
  customElements.define(
    'odp-paged-rail',
    class OdpPagedRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.dots = Array.from(this.querySelectorAll('.odp-pairs__dot'));
        this.pages = Number(this.dataset.pages) || 1;
        if (!this.track || this.pages < 2) return;

        this.index = 0;
        this.ticking = false;

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.sync();
          });
        };
        this.onPrev = () => this.goTo(this.index - 1);
        this.onNext = () => this.goTo(this.index + 1);
        this.onKey = (event) => {
          if (event.target.closest('input, textarea, select')) return;
          if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
        };

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        this.track.addEventListener('keydown', this.onKey);

        this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, true));
        this.resizeObserver.observe(this.track);

        this.sync();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.track?.removeEventListener('keydown', this.onKey);
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        this.resizeObserver?.disconnect();
      }

      get width() {
        return this.track.clientWidth || 1;
      }

      goTo(i, instant = false) {
        const target = Math.max(0, Math.min(this.pages - 1, i));
        const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        this.track.scrollTo({ left: target * this.width * dir, behavior: instant ? 'auto' : undefined });
        // scroll-behavior (smooth / reduced motion) is set in CSS
        this.setIndex(target);
      }

      sync() {
        this.setIndex(Math.round(Math.abs(this.track.scrollLeft) / this.width));
      }

      setIndex(i) {
        this.index = Math.max(0, Math.min(this.pages - 1, i));
        if (this.prev) this.prev.disabled = this.index === 0;
        if (this.next) this.next.disabled = this.index === this.pages - 1;
        this.dots.forEach((dot, j) => dot.classList.toggle('is-active', j === this.index));
      }
    }
  );
}
/* ========================== END ODP CUSTOM — Pairs well with ========================== */
/* ==========================================================================
   ODP CUSTOM — Variant option cards: instant stock / price-difference update
   Append to assets/odp-custom.js
   The section render that follows each click (product-info.js morph) rewrites the same text from
   Liquid; this only makes it correct in the same frame as the click, from the variants cache.
   ========================================================================== */
(() => {
  // Keep in step with the odp_str_* assigns in snippets/product-variant-options.liquid.
  const ODP_VCARD_STRINGS = {
    in: 'In stock',
    order: 'Available to order',
    out: 'Sold out',
    na: 'Unavailable'
  };

  const formatDelta = (cents) => {
    const format = window.FoxTheme?.settings?.moneyFormat;
    const money = window.FoxTheme?.Currency?.formatMoney;
    if (!money || !format) return '';
    // Same as Liquid's money_without_trailing_zeros: drop a ".00" / ",00" decimal part only.
    const text = money(Math.abs(cents), format).replace(/[.,]00(?!\d)/, '');
    return `${cents > 0 ? '+' : '−'} ${text}`;
  };

  const stateOf = (variant) => {
    if (!variant) return 'na';
    if (!variant.available || variant.in_stock === false) return 'out';
    if (variant.inventory && variant.inventory.status === 'continue') return 'order';
    return 'in';
  };

  const matches = (variant, values) =>
    variant.options.length === values.length && variant.options.every((o, i) => o === values[i]);

  const refresh = (picker) => {
    if (typeof picker.getVariantsCache !== 'function') return;
    const variants = picker.getVariantsCache();
    if (!variants || !variants.length) return;

    const groups = Array.from(picker.querySelectorAll(':scope > .product-form__input'));
    if (groups.length !== variants[0].options.length) return;
    if (!picker.querySelector(':scope > .odp-vcards')) return;

    const complete = picker.querySelector('[data-variants-cache]')?.dataset.complete === 'true';
    const selected = picker.selectedOptions.map(({ value }) => value);
    if (selected.length !== groups.length) return;
    const base = variants.find((v) => matches(v, selected)) || null;

    groups.forEach((group, index) => {
      if (!group.classList.contains('odp-vcards')) return;

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = input.nextElementSibling;
        if (!label || !label.classList.contains('odp-vcard')) return;
        // Combined-listing value: another product's variants, not in this cache.
        if (input.dataset.productUrl && input.dataset.productUrl !== picker.dataset.url) return;

        const combo = selected.slice();
        combo[index] = input.value;
        const variant = variants.find((v) => matches(v, combo)) || null;
        // Past 250 variants the cache is partial: a miss means "not cached", not "doesn't exist".
        if (!variant && !complete) return;

        const state = stateOf(variant);
        label.dataset.odpState = state;
        const stock = label.querySelector('[data-odp-stock]');
        if (stock) stock.textContent = ODP_VCARD_STRINGS[state];

        const delta = label.querySelector('[data-odp-delta]');
        if (delta) {
          const diff = variant && base && !input.checked ? variant.price - base.price : 0;
          delta.textContent = diff ? formatDelta(diff) : '';
        }
      });
    });
  };

  // Bubble phase: variant-selects has already marked the new selection (it listens in capture).
  document.addEventListener('change', (event) => {
    const picker = event.target.closest?.('variant-selects');
    if (picker && picker.querySelector('.odp-vcards')) refresh(picker);
  });
})();
/* ========================== END ODP CUSTOM — Variant option cards ========================== */